<script setup>
import { computed, defineModel, defineProps, nextTick, ref, watch } from 'vue';
import LoadingSvg from '@/components/UI/LoadingSvg.vue';

const selected = defineModel({ default: () => [] });

const props = defineProps({
    values: {
        type: Array,
        required: true,
    },
    label: {
        type: String,
        required: true,
    },
    labelKey: {
        type: String,
        default: 'name',
    },
    valueKey: {
        type: String,
        default: 'id',
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
    isLoadingLabel: {
        type: String,
        default: 'Загрузка...',
    },
    searchable: {
        type: Boolean,
        default: true,
    },
    searchPlaceholder: {
        type: String,
        default: 'Поиск...',
    },
    showSelectAll: {
        type: Boolean,
        default: false,
    },
});

const isOpen = ref(false);
const searchQuery = ref('');
const searchInputRef = ref(null);

const displayValue = computed(() => {
    if (!selected.value?.length) {
        return props.label;
    }

    return selected.value
        .map((item) => item[props.labelKey])
        .join(', ');
});

const filteredValues = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) {
        return props.values;
    }

    return props.values.filter((item) => {
        const label = String(item?.[props.labelKey] ?? '').toLowerCase();
        const value = String(item?.[props.valueKey] ?? '').toLowerCase();
        return label.includes(query) || value.includes(query);
    });
});

const areAllFilteredSelected = computed(() => {
    if (!filteredValues.value.length) {
        return false;
    }

    return filteredValues.value.every((item) => isSelected(item));
});

function isSelected(item) {
    return selected.value.some((selectedItem) => selectedItem[props.valueKey] === item[props.valueKey]);
}

function toggleItem(item) {
    if (isSelected(item)) {
        selected.value = selected.value.filter(
            (selectedItem) => selectedItem[props.valueKey] !== item[props.valueKey],
        );
        return;
    }

    selected.value = [...selected.value, item];
}

function toggleSelectAll() {
    if (areAllFilteredSelected.value) {
        const filteredIds = new Set(filteredValues.value.map((item) => item[props.valueKey]));
        selected.value = selected.value.filter((item) => !filteredIds.has(item[props.valueKey]));
        return;
    }

    const nextSelected = [...selected.value];
    filteredValues.value.forEach((item) => {
        if (!nextSelected.some((selectedItem) => selectedItem[props.valueKey] === item[props.valueKey])) {
            nextSelected.push(item);
        }
    });
    selected.value = nextSelected;
}

async function toggleDropdown() {
    if (props.isLoading) {
        return;
    }

    isOpen.value = !isOpen.value;

    if (isOpen.value) {
        await nextTick();
        searchInputRef.value?.focus();
    } else {
        searchQuery.value = '';
    }
}

function closeDropdown() {
    isOpen.value = false;
    searchQuery.value = '';
}

watch(
    () => props.isLoading,
    (loading) => {
        if (loading) {
            closeDropdown();
        }
    },
);
</script>

<template>
    <div
        class="relative w-full"
        v-click-outside="closeDropdown"
    >
        <button
            type="button"
            class="w-full min-h-12 border rounded-sm px-4 py-2 text-left bg-white flex items-center justify-between gap-3 transition-opacity duration-200"
            :class="{
                'border-black': !isLoading,
                'border-gray-400 cursor-default opacity-80': isLoading,
            }"
            :disabled="isLoading"
            @click="toggleDropdown"
        >
            <span
                v-if="isLoading"
                class="block overflow-hidden text-ellipsis whitespace-nowrap text-gray-400"
            >
                {{ isLoadingLabel }}
            </span>
            <span
                v-else
                class="block overflow-hidden text-ellipsis whitespace-nowrap"
                :class="selected.length ? 'text-gray-900' : 'text-gray-500'"
            >
                {{ displayValue }}
            </span>
            <LoadingSvg
                v-if="isLoading"
                class="size-4 flex-shrink-0 animate-spin text-blue-500"
            />
            <svg
                v-else
                class="w-4 h-4 flex-shrink-0 transition-transform"
                :class="{ 'rotate-180': isOpen }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
        </button>

        <div
            v-if="isOpen && !isLoading"
            class="absolute z-40 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
        >
            <div
                v-if="searchable"
                class="sticky top-0 z-10 bg-white border-b border-gray-100 p-2"
            >
                <input
                    ref="searchInputRef"
                    v-model="searchQuery"
                    type="text"
                    class="w-full h-9 px-3 text-sm border border-gray-200 rounded-md outline-none focus:border-blue-400"
                    :placeholder="searchPlaceholder"
                    @click.stop
                >
            </div>

            <button
                v-if="showSelectAll && filteredValues.length"
                type="button"
                class="w-full px-4 py-2.5 text-left text-sm font-semibold text-blue-600 hover:bg-blue-50 border-b border-gray-100"
                @click="toggleSelectAll"
            >
                {{ areAllFilteredSelected ? 'Снять всё' : 'Выбрать всё' }}
            </button>

            <div class="max-h-60 overflow-y-auto">
                <button
                    v-for="item in filteredValues"
                    :key="item[valueKey]"
                    type="button"
                    class="w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-gray-50"
                    :class="{ 'bg-blue-50 text-blue-700': isSelected(item) }"
                    @click="toggleItem(item)"
                >
                    <span
                        class="size-4 rounded border flex items-center justify-center flex-shrink-0"
                        :class="isSelected(item) ? 'border-blue-500 bg-blue-500 text-white' : 'border-gray-300'"
                    >
                        <svg
                            v-if="isSelected(item)"
                            class="w-3 h-3"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </span>
                    <span class="overflow-hidden text-ellipsis whitespace-nowrap">{{ item[labelKey] }}</span>
                </button>

                <div
                    v-if="!filteredValues.length"
                    class="px-4 py-3 text-sm text-gray-400"
                >
                    Ничего не найдено
                </div>
            </div>
        </div>
    </div>
</template>
